import alt from 'components/Dispatcher';
import {Map, List, Record} from 'immutable';
import immutableStore from 'alt-utils/lib/ImmutableUtil';
import User from 'records/User';
import CallActions from 'actions/CallActions';
import CallerTeamComboboxSearchFieldActions from 'actions/search/CallerTeamComboboxSearchFieldActions';
import CallerTeamComboboxSearchFieldSource from 'sources/search/CallerTeamComboboxSearchFieldSource';
import ConstValues from 'utils/const';

const SelectedOptionKeyToState = Map({
  team_search: 'selectedTeams',
  rep_search: 'selectedReps',
  manager_search: 'selectedManagers',
});

const State = Record({
  teams: List(),
  reps: List(),
  managers: List(),
  selectedTeams: List(),
  selectedReps: List(),
  selectedManagers: List(),
  canToggleCallerField: true,
});

@immutableStore
class CallerTeamComboboxSearchFieldStore {
  constructor() {
    this.state = new State();
    this.bindActions(CallActions);
    this.bindActions(CallerTeamComboboxSearchFieldActions);
    this.registerAsync(CallerTeamComboboxSearchFieldSource);
  }

  onUpdateSearchType(args) {
    const [, searchType] = args;
    const st = ConstValues.searchTypes;
    const state = this.state.set('canToggleCallerField', searchType !== st.miCaller);
    this.setState(state);
  }

  onClearSearch() {
    this._resetState();
  }

  onResetCaller() {
    this._resetState();
  }

  _resetState() {
    this.setState(State());
  }

  onFetchSelectedTeams(teamIds) {
    setTimeout(() => this.getInstance().fetchTeamList(teamIds), 0);
  }

  onFetchSelectedTeamsSuccess(resp) {
    this._updateStateWithData(resp.data.teams, 'selectedTeams');
  }

  onFetchSelectedManagers(managerIds) {
    setTimeout(() => this.getInstance().fetchManagerList(managerIds), 0);
  }

  onFetchSelectedManagersSuccess(resp) {
    this._updateStateWithData(resp.data.users, 'selectedManagers');
  }

  onFetchSelectedReps(repIds) {
    setTimeout(() => this.getInstance().fetchUserList(repIds), 0);
  }

  onFetchSelectedRepsSuccess(resp) {
    this._updateStateWithData(resp.data.users, 'selectedReps');
  }

  static selectedOptions(state, selectedOptionKey) {
    if (selectedOptionKey === 'team_search') {
      return state.get('selectedTeams');
    } else if (selectedOptionKey === 'rep_search') {
      return state.get('selectedReps');
    } else if (selectedOptionKey === 'manager_search') {
      return state.get('selectedManagers');
    }
    return null;
  }

  formatUserTypeCallerForSearchSelectedList(caller) {
    return {
      first_name: caller.first_name,
      last_name: caller.last_name,
      type: 'user',
      id: caller.id,
      name: caller.first_name + ' ' + caller.last_name,
    };
  }

  onChangeCaller(args) {
    const {caller, selectedOptionKey} = args;
    let state = this.state;
    if (selectedOptionKey === 'team_search') {
      const selectedTeams = state.get('selectedTeams').push(caller);
      state = state.set('selectedTeams', selectedTeams);
    } else if (selectedOptionKey === 'rep_search') {
      const selectedReps = state
        .get('selectedReps')
        .push(this.formatUserTypeCallerForSearchSelectedList(caller));
      state = state.set('selectedReps', selectedReps);
    } else if (selectedOptionKey === 'manager_search') {
      const selectedReps = state
        .get('selectedManagers')
        .push(this.formatUserTypeCallerForSearchSelectedList(caller));
      state = state.set('selectedManagers', selectedReps);
    }
    state = this._clearOtherSelectedOptions(selectedOptionKey, state);
    this.setState(state);
  }

  /** Remove other selected options since the user has chosen a caller value for `selectedOptionKey` */
  _clearOtherSelectedOptions(selectedOptionKey, state) {
    const fieldsToClear = SelectedOptionKeyToState.filter((_, key) => {
      return key !== selectedOptionKey;
    }).valueSeq();
    fieldsToClear.forEach(stateField => {
      state = state.set(stateField, List());
    });
    return state;
  }

  onRemoveCaller(args) {
    const {caller, selectedOptionKey} = args;
    let state = this.state;
    if (selectedOptionKey === 'team_search') {
      const selectedReps = state.get('selectedTeams').filterNot(team => team.id === caller.id);
      state = state.set('selectedTeams', selectedReps);
    } else if (selectedOptionKey === 'rep_search') {
      const selectedReps = state.get('selectedReps').filterNot(rep => rep.id === caller.id);
      state = state.set('selectedReps', selectedReps);
    } else if (selectedOptionKey === 'manager_search') {
      const selectedReps = state.get('selectedManagers').filterNot(rep => rep.id === caller.id);
      state = state.set('selectedManagers', selectedReps);
    }
    this.setState(state);
  }

  onSearchReps(searchTerm) {
    setTimeout(() => this.getInstance().searchReps(searchTerm), 0);
  }

  onSearchRepsSuccess({data: {users}}) {
    this._updateStateWithData(users && users.map(user => new User(user)), 'reps');
  }

  onSearchManagers(searchTerm) {
    setTimeout(() => this.getInstance().searchManagers(searchTerm), 0);
  }

  onSearchManagersSuccess({data: {users}}) {
    this._updateStateWithData(users && users.map(user => new User(user)), 'managers');
  }

  onSearchTeams(searchTerm) {
    setTimeout(() => this.getInstance().searchTeams(searchTerm), 0);
  }

  onSearchTeamsSuccess(resp) {
    this._updateStateWithData(resp.data.teams, 'teams');
  }

  _updateStateWithData(items, field) {
    if (items) {
      const state = this.state.set(field, List(items));
      this.setState(state);
    }
  }
}

export default alt.createStore(
  CallerTeamComboboxSearchFieldStore,
  'CallerTeamComboboxSearchFieldStore'
);
